import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SeoMeta from "../SeoMeta";
import { getSummary } from "../../apis/UserApis";
import AdminJobsTab from "./AdminJobsTab";
import AdminUsersTab from "./AdminUsersTab";
import AdminCompaniesTab from "./AdminCompaniesTab";
import AdminAnalyticsTab from "./AdminAnalyticsTab";
import { useTheme } from "../Theme/ThemeContext";

const TABS = [
  { id: "analytics", label: "Analytics", icon: "ri-bar-chart-box-line" },
  { id: "jobs", label: "Jobs", icon: "ri-briefcase-line" },
  { id: "users", label: "Users", icon: "ri-user-line" },
  { id: "companies", label: "Companies", icon: "ri-building-line" },
];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("analytics");
  const [summary, setSummary] = useState(null);
  const { currentTheme } = useTheme();

  useEffect(() => {
    getSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  const pageTitle = `Admin Dashboard | Code Arena`;
  const pageDescription = `Manage jobs, users, and companies on Code Arena admin panel.`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
      <SeoMeta
        title={pageTitle}
        description={pageDescription}
        url="https://www.code-arena.in/admin"
        image="/logo192.png"
      />

      {/* Header */}
      <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} text-white`}>
        <div className="ca-container py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-white/80">
                Manage all your platform data in one place
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/admin/add/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-accent-700 font-semibold rounded-xl hover:bg-accent-50 transition-colors shadow-lg shadow-accent-500/25"
              >
                <i className="ri-add-line"></i> Add New Job
              </Link>
              <Link
                to="/admin/purchases/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-colors backdrop-blur-sm"
              >
                <i className="ri-shopping-cart-line"></i> Create Purchase
              </Link>
            </div>
          </div>

          {/* Quick Stats */}
          {summary && (
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
                <div className="text-2xl font-bold">
                  {summary.totalJobs?.toLocaleString() || 0}
                </div>
                <div className="text-white/70 text-sm">Total Jobs</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
                <div className="text-2xl font-bold">
                  {summary.totalApplications?.toLocaleString() || 0}
                </div>
                <div className="text-white/70 text-sm">Applications</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
                <div className="text-2xl font-bold">
                  {summary.totalRegisteredUsers?.toLocaleString() || 0}
                </div>
                <div className="text-white/70 text-sm">Registered Users</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="glass border-b border-white/20 dark:border-white/10 sticky top-0 z-30">
        <div className="ca-container">
          <nav className="flex gap-1 overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-accent-500 text-accent-600 dark:text-accent-400"
                    : "border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:border-neutral-300 dark:hover:border-neutral-600"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="ca-container py-8">
        {activeTab === "analytics" && <AdminAnalyticsTab />}
        {activeTab === "jobs" && <AdminJobsTab />}
        {activeTab === "users" && <AdminUsersTab />}
        {activeTab === "companies" && <AdminCompaniesTab />}
      </div>
    </div>
  );
}
